package com.sddamico.draftmachine.pages.league

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.components.layouts.LoadingFrame
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.sections.AppHeader
import com.sddamico.draftmachine.components.widgets.SelectableCard
import com.sddamico.draftmachine.components.widgets.SyncStatusBadge
import com.sddamico.draftmachine.data.ExternalProvider
import com.sddamico.draftmachine.data.OwnershipSyncStatus
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.core.Card
import com.sddamico.draftmachine.ds.core.CardVariant
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.feedback.Toast
import com.sddamico.draftmachine.ds.feedback.ToastTone
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.forms.Select
import com.sddamico.draftmachine.ds.roleBody
import com.sddamico.draftmachine.ds.roleH2
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.roleMono
import com.sddamico.draftmachine.models.EspnLeaguePreview
import com.sddamico.draftmachine.models.FantraxLeagueOption
import com.sddamico.draftmachine.models.LeagueConnectionResponse
import com.sddamico.draftmachine.models.OwnershipSyncResponse
import com.sddamico.draftmachine.util.EspnLookupRequest
import com.sddamico.draftmachine.util.ESPN_SUPPORTED_SPORTS
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.LookupEspnLeagueResult
import com.sddamico.draftmachine.util.LookupLeaguesResult
import com.sddamico.draftmachine.util.SaveConnectionResult
import com.sddamico.draftmachine.util.TriggerSyncResult
import com.sddamico.draftmachine.util.fetchLeagueConnection
import com.sddamico.draftmachine.util.compatibleFantraxLeagues
import com.sddamico.draftmachine.util.compatibleProviders
import com.sddamico.draftmachine.util.formatStampDateTime
import com.sddamico.draftmachine.util.lookupEspnLeague
import com.sddamico.draftmachine.util.lookupFantraxLeagues
import com.sddamico.draftmachine.util.rememberAuthState
import com.sddamico.draftmachine.util.saveLeagueConnection
import com.sddamico.draftmachine.util.sportLabel
import com.sddamico.draftmachine.util.supports
import com.sddamico.draftmachine.util.transitionTo
import com.sddamico.draftmachine.util.triggerOwnershipSync
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Text

private fun currentBrowserYear(): Int = js("new Date().getFullYear()")
private fun currentBrowserMonth(): Int = js("new Date().getMonth()")
private fun defaultEspnSeason(sport: Sport): Int =
  if (sport in listOf(Sport.NBA, Sport.NHL) && currentBrowserMonth() >= 6) currentBrowserYear() + 1
  else currentBrowserYear()

@Page("{id}/settings")
@Composable
fun LeagueSettingsPage() {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()
  val leagueId = ctx.route.params["id"] ?: ctx.route.path.substringAfter("/league/").substringBefore("/")

  when (val state = auth.value) {
    is LoadState.Loading -> LoadingFrame()
    is LoadState.Failed -> {
      LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      LoadingFrame()
    }
    is LoadState.Ready -> {
      val user = state.value
      if (user == null) {
        LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
        LoadingFrame()
      } else {
        MobileFrame(
          modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
          header = { AppHeader(email = user.email, title = "League settings", onBack = { ctx.router.transitionTo("/") }) },
        ) {
          Div(attrs = Modifier.padding(DmSpacing.space6).toAttrs()) {
            LeagueConnectionPanel(leagueId)
          }
        }
      }
    }
  }
}

/** Provider-aware league connection editor reused by league and draft settings. */
@Composable
fun LeagueConnectionPanel(leagueId: String, requiredSport: Sport? = null) {
  val scope = rememberCoroutineScope()
  val availableProviders = compatibleProviders(requiredSport)
  val initialProvider = availableProviders.firstOrNull() ?: ExternalProvider.FANTRAX
  val initialEspnSport = requiredSport?.takeIf { ExternalProvider.ESPN.supports(it) } ?: Sport.NFL
  var leagueName by remember(leagueId) { mutableStateOf("") }
  var connection by remember(leagueId) { mutableStateOf<LeagueConnectionResponse?>(null) }
  var latestSync by remember(leagueId) { mutableStateOf<OwnershipSyncResponse?>(null) }
  var loadError by remember(leagueId) { mutableStateOf<String?>(null) }
  var loaded by remember(leagueId) { mutableStateOf(false) }

  var provider by remember(leagueId, requiredSport) { mutableStateOf(initialProvider) }
  var secret by remember(leagueId) { mutableStateOf("") }
  var options by remember(leagueId) { mutableStateOf<List<FantraxLeagueOption>>(emptyList()) }
  var espnLeagueId by remember(leagueId) { mutableStateOf("") }
  var espnSport by remember(leagueId, requiredSport) { mutableStateOf(initialEspnSport) }
  var espnSeason by remember(leagueId, requiredSport) {
    mutableStateOf(defaultEspnSeason(initialEspnSport).toString())
  }
  var espnS2 by remember(leagueId) { mutableStateOf("") }
  var espnSwid by remember(leagueId) { mutableStateOf("") }
  var espnPreview by remember(leagueId) { mutableStateOf<EspnLeaguePreview?>(null) }
  var looking by remember(leagueId) { mutableStateOf(false) }
  var lookupError by remember(leagueId) { mutableStateOf<String?>(null) }
  var espnLookupGeneration by remember(leagueId) { mutableStateOf(0) }
  var saving by remember(leagueId) { mutableStateOf(false) }
  var saveError by remember(leagueId) { mutableStateOf<String?>(null) }
  var saved by remember(leagueId) { mutableStateOf(false) }
  var syncing by remember(leagueId) { mutableStateOf(false) }
  var syncError by remember(leagueId) { mutableStateOf<String?>(null) }

  suspend fun reload() {
    when (val result = fetchLeagueConnection(leagueId)) {
      is LoadState.Ready -> {
        leagueName = result.value.leagueName
        connection = result.value.connection
        latestSync = result.value.latestSync
        if (!loaded) {
          result.value.connection?.let { current ->
            provider = current.provider.takeIf { it in availableProviders } ?: initialProvider
            if (current.provider == ExternalProvider.ESPN) {
              espnLeagueId = current.externalLeagueId
              if (requiredSport == null) espnSport = current.sport
              espnSeason = current.externalSeason?.toString() ?: defaultEspnSeason(espnSport).toString()
            }
          }
        }
        loadError = null
      }
      is LoadState.Failed -> loadError = result.message
      is LoadState.Loading -> Unit
    }
    loaded = true
  }

  suspend fun startSync() {
    when (val result = triggerOwnershipSync(leagueId)) {
      is TriggerSyncResult.Success -> latestSync = result.sync
      is TriggerSyncResult.Failure -> syncError = result.message
    }
  }

  LaunchedEffect(leagueId) { reload() }
  LaunchedEffect(latestSync?.id, latestSync?.status) {
    val status = latestSync?.status
    if (status != OwnershipSyncStatus.RUNNING && status != OwnershipSyncStatus.PENDING) return@LaunchedEffect
    while (true) {
      delay(3_000)
      reload()
      val next = latestSync?.status
      if (next != OwnershipSyncStatus.RUNNING && next != OwnershipSyncStatus.PENDING) break
    }
  }

  fun confirmReplacement(nextProvider: ExternalProvider, nextLeagueId: String, nextSeason: Int?): Boolean {
    val current = connection ?: return true
    if (
      current.provider == nextProvider && current.externalLeagueId == nextLeagueId &&
      current.externalSeason == nextSeason
    ) return true
    return window.confirm("Change the external league? The current ownership snapshot will be replaced.")
  }

  fun invalidateEspnLookup() {
    espnLookupGeneration += 1
    looking = false
    lookupError = null
    espnPreview = null
  }

  fun selectProvider(nextProvider: ExternalProvider) {
    if (provider != nextProvider) invalidateEspnLookup()
    provider = nextProvider
  }

  fun setEspnLeagueId(value: String) {
    if (espnLeagueId != value) invalidateEspnLookup()
    espnLeagueId = value
  }

  fun setEspnSport(value: Sport) {
    if (espnSport != value) invalidateEspnLookup()
    espnSport = value
    espnSeason = defaultEspnSeason(value).toString()
  }

  fun setEspnSeason(value: String) {
    if (espnSeason != value) invalidateEspnLookup()
    espnSeason = value
  }

  fun setEspnS2(value: String) {
    if (espnS2 != value) invalidateEspnLookup()
    espnS2 = value
  }

  fun setEspnSwid(value: String) {
    if (espnSwid != value) invalidateEspnLookup()
    espnSwid = value
  }

  fun isCurrentEspnLookup(request: EspnLookupRequest): Boolean = request.matches(
    currentGeneration = espnLookupGeneration,
    enabled = true,
    provider = provider,
    leagueId = espnLeagueId,
    sport = espnSport,
    season = espnSeason.toIntOrNull(),
    espnS2 = espnS2,
    espnSwid = espnSwid,
  )

  fun lookupFantrax() {
    if (looking || secret.isBlank()) return
    looking = true
    lookupError = null
    scope.launch {
      when (val result = lookupFantraxLeagues(secret.trim())) {
        is LookupLeaguesResult.Success -> {
          options = compatibleFantraxLeagues(result.leagues, requiredSport)
          if (options.isEmpty()) {
            lookupError = if (result.leagues.isEmpty() || requiredSport == null) {
              "That secret id didn't return any leagues."
            } else {
              "That secret id didn't return any ${sportLabel(requiredSport)} leagues."
            }
          }
        }
        is LookupLeaguesResult.Failure -> lookupError = result.message
      }
      looking = false
    }
  }

  fun lookupEspn() {
    val season = espnSeason.toIntOrNull()
    if (looking || season == null || espnLeagueId.isBlank() || espnS2.isBlank() || espnSwid.isBlank()) return
    val requestedLeagueId = espnLeagueId.trim()
    val requestedSport = espnSport
    val requestedS2 = espnS2.trim()
    val requestedSwid = espnSwid.trim()
    espnLookupGeneration += 1
    val request = EspnLookupRequest(
      espnLookupGeneration, requestedLeagueId, requestedSport, season, requestedS2, requestedSwid,
    )
    looking = true
    lookupError = null
    scope.launch {
      when (val result = lookupEspnLeague(requestedLeagueId, requestedSport, season, requestedS2, requestedSwid)) {
        is LookupEspnLeagueResult.Success -> if (isCurrentEspnLookup(request)) espnPreview = result.league
        is LookupEspnLeagueResult.Failure -> if (isCurrentEspnLookup(request)) lookupError = result.message
      }
      if (request.generation == espnLookupGeneration) looking = false
    }
  }

  fun finishSave(result: SaveConnectionResult) {
    when (result) {
      is SaveConnectionResult.Success -> {
        connection = result.connection
        saved = true
        options = emptyList()
        espnPreview = null
        scope.launch { startSync() }
      }
      is SaveConnectionResult.Failure -> saveError = result.message
    }
    saving = false
  }

  fun connectFantrax(option: FantraxLeagueOption) {
    val sport = option.sport ?: run { saveError = "The Draft Machine doesn't handle that sport yet."; return }
    if (requiredSport != null && sport != requiredSport) {
      saveError = "Choose a ${sportLabel(requiredSport)} Fantrax league."
      return
    }
    if (saving || !confirmReplacement(ExternalProvider.FANTRAX, option.leagueId, null)) return
    saving = true
    saveError = null
    saved = false
    scope.launch {
      finishSave(
        saveLeagueConnection(
          leagueId = leagueId,
          provider = ExternalProvider.FANTRAX,
          externalLeagueId = option.leagueId,
          sport = sport,
          userSecretId = secret.trim().ifEmpty { null },
        )
      )
    }
  }

  fun connectEspn() {
    val preview = espnPreview ?: return
    if (requiredSport != null && preview.sport != requiredSport) {
      saveError = "Choose a ${sportLabel(requiredSport)} ESPN league."
      return
    }
    if (saving || !confirmReplacement(ExternalProvider.ESPN, preview.leagueId, preview.season)) return
    saving = true
    saveError = null
    saved = false
    scope.launch {
      finishSave(
        saveLeagueConnection(
          leagueId = leagueId,
          provider = ExternalProvider.ESPN,
          externalLeagueId = preview.leagueId,
          externalSeason = preview.season,
          sport = preview.sport,
          espnS2 = espnS2.trim(),
          espnSwid = espnSwid.trim(),
        )
      )
    }
  }

  fun sync() {
    if (syncing) return
    syncing = true
    syncError = null
    scope.launch {
      startSync()
      syncing = false
    }
  }

  SpanText(leagueName.ifBlank { "League connection" }, Modifier.roleH2().displayBlock().margin(bottom = DmSpacing.space6))
  loadError?.let {
    SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(bottom = DmSpacing.space4))
  }
  if (saved) {
    Toast(
      title = "League connected", message = "Ownership sync started.", tone = ToastTone.Verde,
      onClose = { saved = false }, modifier = Modifier.margin(bottom = DmSpacing.space4),
    )
  }

  if (loaded) {
    Card(variant = CardVariant.Raised, riveted = true) {
      connection?.let { current ->
        SettingRow("Provider", current.provider.name)
        Rule()
        SettingRow("External league id", current.externalLeagueId)
        current.externalSeason?.let { Rule(); SettingRow("Season", it.toString()) }
        Rule()
        SettingRow("Sport", sportLabel(current.sport))
        Rule()
        SettingRow("Credentials", if (current.hasCredentials) "Stored" else "Missing")
        Rule()
      }

      if (availableProviders.isEmpty()) {
        SpanText(
          "League ownership linking isn't available for ${requiredSport?.let(::sportLabel) ?: "this draft"}.",
          Modifier.roleBody().color(DmColor.textSecondary).displayBlock(),
        )
        return@Card
      }

      SpanText(
        if (connection == null) "CONNECT A LEAGUE" else "CHANGE THE CONNECTION",
        Modifier.dmStamp().color(DmColor.brass300).displayBlock().margin(bottom = DmSpacing.space3),
      )
      Div(attrs = Modifier.displayFlex().gap(8.px).margin(bottom = DmSpacing.space4).toAttrs()) {
        if (ExternalProvider.FANTRAX in availableProviders) {
          ProviderPlate("Fantrax", "broadcast", provider == ExternalProvider.FANTRAX) {
            selectProvider(ExternalProvider.FANTRAX)
          }
        }
        if (ExternalProvider.ESPN in availableProviders) {
          ProviderPlate("ESPN", "television-simple", provider == ExternalProvider.ESPN) {
            selectProvider(ExternalProvider.ESPN)
          }
        }
      }

      when (provider) {
        ExternalProvider.FANTRAX -> {
          Input(
            label = "Fantrax user secret id", icon = "key",
            hint = "From your Fantrax profile page. We use it to list your leagues.",
            error = lookupError, value = secret,
            onValueChange = { secret = it; lookupError = null }, onEnter = ::lookupFantrax,
          )
          ActionRow {
            Button(
              "Find my leagues", icon = "magnifying-glass", variant = ButtonVariant.Secondary,
              loading = looking, disabled = secret.isBlank(), onClick = ::lookupFantrax,
            )
          }
          options.forEach { option -> LeagueOption(option) { connectFantrax(option) } }
        }
        ExternalProvider.ESPN -> {
          Input(
            label = "ESPN league id", icon = "hash", value = espnLeagueId,
            onValueChange = ::setEspnLeagueId,
          )
          if (requiredSport == null) {
            Select(label = "ESPN sport", onValueChange = { value ->
              setEspnSport(Sport.valueOf(value))
            }) {
              ESPN_SUPPORTED_SPORTS.forEach { sport ->
                Option(sport.name, attrs = { if (sport == espnSport) attr("selected", "selected") }) {
                  Text(sportLabel(sport))
                }
              }
            }
          } else {
            SettingRow("ESPN sport", sportLabel(requiredSport))
          }
          Input(
            label = "ESPN season", icon = "calendar-blank",
            hint = "NBA and NHL use the season-ending year shown in the ESPN league URL.",
            value = espnSeason, onValueChange = ::setEspnSeason,
          )
          Input(
            label = "espn_s2 cookie", icon = "key", type = "password",
            hint = "Copy the value from your signed-in ESPN browser cookies.",
            value = espnS2, onValueChange = ::setEspnS2,
          )
          Input(
            label = "SWID cookie", icon = "fingerprint", error = lookupError,
            value = espnSwid, onValueChange = ::setEspnSwid,
            onEnter = ::lookupEspn,
          )
          ActionRow {
            Button(
              "Check league", icon = "magnifying-glass", variant = ButtonVariant.Secondary,
              loading = looking,
              disabled = espnLeagueId.isBlank() || espnSeason.toIntOrNull() == null ||
                espnS2.isBlank() || espnSwid.isBlank(),
              onClick = ::lookupEspn,
            )
          }
          espnPreview?.let { preview ->
            SelectableCard(selected = true, onClick = null, modifier = Modifier.margin(top = DmSpacing.space3)) {
              Icon("check-circle", IconWeight.Fill, Modifier.fontSize(20.px).color(DmColor.brass400))
              Div(attrs = Modifier.flex(1).toAttrs()) {
                SpanText(preview.leagueName, Modifier.roleH3().displayBlock())
                SpanText(
                  "${sportLabel(preview.sport)} · ${preview.season} · ${preview.teams.size} teams",
                  Modifier.roleMono().fontSize(11.px).color(DmColor.textMuted).displayBlock(),
                )
              }
            }
            ActionRow {
              Button("Connect ESPN", icon = "plug", loading = saving, onClick = ::connectEspn)
            }
          }
        }
      }
      saveError?.let {
        SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
      }
    }

    if (connection != null) {
      SpanText("Ownership sync", Modifier.roleH2().displayBlock().margin(top = DmSpacing.space8, bottom = DmSpacing.space4))
      Card(variant = CardVariant.Raised, riveted = true) {
        Div(
          attrs = Modifier.displayFlex().alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.SpaceBetween).margin(bottom = DmSpacing.space4).toAttrs()
        ) {
          SyncStatusBadge(latestSync?.status)
          Button(
            "Sync now", icon = "arrows-clockwise", size = ButtonSize.Sm,
            loading = syncing || latestSync?.status == OwnershipSyncStatus.RUNNING, onClick = ::sync,
          )
        }
        val sync = latestSync
        if (sync != null) {
          SettingRow("Players owned", sync.ownedCount.toString()); Rule()
          SettingRow("Matched to our catalog", sync.matchedCount.toString()); Rule()
          SettingRow("Unmatched", sync.unmatchedCount.toString()); Rule()
          SettingRow("Teams mapped", sync.teamsMappedCount.toString())
          sync.finishedAt?.let { Rule(); SettingRow("Last finished", formatStampDateTime(it)) }
          sync.errorMessage?.let { Rule(); SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock()) }
        } else {
          SpanText("This league has never been synced.", Modifier.roleBody().color(DmColor.textSecondary).displayBlock())
        }
        syncError?.let {
          SpanText(it, Modifier.roleBody().color(DmColor.rust300).displayBlock().margin(top = DmSpacing.space3))
        }
      }
    }
  }
}

@Composable
private fun ProviderPlate(label: String, icon: String, selected: Boolean, onClick: () -> Unit) {
  SelectableCard(
    selected = selected, onClick = onClick, modifier = Modifier.flex(1).padding(10.px), ariaLabel = "Use $label",
  ) {
    Icon(icon, IconWeight.Bold, Modifier.fontSize(18.px).color(DmColor.brass400))
    SpanText(label, Modifier.dmStamp())
  }
}

@Composable
private fun ActionRow(content: @Composable () -> Unit) {
  Div(attrs = Modifier.displayFlex().justifyContent(JustifyContent.FlexEnd).margin(top = DmSpacing.space3).toAttrs()) {
    content()
  }
}

@Composable
private fun LeagueOption(option: FantraxLeagueOption, onSelect: () -> Unit) {
  Div(
    attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(2.px)
      .padding(topBottom = 10.px, leftRight = 12.px).margin(top = DmSpacing.space3)
      .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(6.px)
      .backgroundColor(DmColor.surfaceInset).cursor(Cursor.Pointer).toAttrs {
        attr("role", "button"); onClick { onSelect() }
      },
  ) {
    SpanText(option.leagueName, Modifier.roleBody().color(DmColor.text))
    SpanText(
      listOfNotNull(option.teamName?.let { "Your team: $it" }, option.sport?.let(::sportLabel)).joinToString(" · "),
      Modifier.roleMono().fontSize(11.px).color(DmColor.textMuted),
    )
  }
}

@Composable
private fun SettingRow(label: String, value: String) {
  SpanText(label, Modifier.dmStamp().color(DmColor.brass300).displayBlock().margin(bottom = 4.px))
  SpanText(value, Modifier.roleMono().color(DmColor.text).displayBlock())
}

@Composable
private fun Rule() {
  Div(attrs = Modifier.height(1.px).backgroundColor(DmColor.border).margin(topBottom = DmSpacing.space4).toAttrs()) {}
}
